import React from "react";
import { PaperAirplaneIcon, PaperClipIcon } from "@heroicons/react/24/outline";
import { INCIDENT_CATEGORIES_WITH_OPTIONS } from "../../constants/AppConstants";

const PRIORITY_COLORS = {
  low: "bg-blue-500 text-white",
  medium: "bg-yellow-500 text-white",
  high: "bg-orange-500 text-white",
  critical: "bg-red-600 text-white",
};

const SubmitConfirmDialog = ({ show, form, user, attachments = [], submitting, onConfirm, onCancel }) => {
  if (!show) return null;

  const selectedCategory = INCIDENT_CATEGORIES_WITH_OPTIONS.find(
    (c) => c.id === form.incident_type
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/50 dark:bg-gray-950/50 backdrop-blur-sm"
        onClick={onCancel}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-primary-700 to-primary-800">
          <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
            <PaperAirplaneIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">Confirm Incident Report</h3>
            <p className="text-xs text-white mt-0.5">
              Please review your details before submitting
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Type */}
          <div className="flex items-start justify-between gap-3 pb-3 border-b border-gray-200 dark:border-gray-700">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-28 shrink-0 pt-0.5">
              Type
            </span>
            <span className="text-xs font-medium text-gray-900 dark:text-white text-right">
              {selectedCategory?.name || "—"}
              {form.incident_subtype && (
                <span className="ml-1 text-primary-600 dark:text-primary-400">
                  · {form.incident_subtype}
                </span>
              )}
            </span>
          </div>

          {/* Priority */}
          <div className="flex items-center justify-between gap-3 pb-3 border-b border-gray-200 dark:border-gray-700">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-28 shrink-0">
              Priority
            </span>
            <span
              className={`text-xs font-bold px-2.5 py-0.5 rounded-full capitalize ${
                PRIORITY_COLORS[form.priority] || "bg-gray-100 text-white"
              }`}
            >
              {form.priority}
            </span>
          </div>

          {/* Reporter */}
          <div className="flex items-center justify-between gap-3 pb-3 border-b border-gray-200 dark:border-gray-700">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-28 shrink-0">
              Reporter
            </span>
            <span className="text-xs font-medium text-gray-900 dark:text-white truncate text-right">
              {user?.email || user?.firstname || "—"}
            </span>
          </div>

          {/* Description */}
          <div className="pt-1 pb-3">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Description
            </p>
            <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600 max-h-24 overflow-y-auto">
              {form.description}
            </p>
          </div>

          {/* Attachments */}
          {attachments.length > 0 && (
            <div className="pt-1">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                Attachments ({attachments.length})
              </p>
              <ul className="space-y-1">
                {attachments.map((file, idx) => (
                  <li
                    key={idx}
                    className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-600"
                  >
                    <PaperClipIcon className="w-3.5 h-3.5 text-primary-600 shrink-0" />
                    <span className="truncate">{file instanceof File ? file.name : file}</span>
                    <span className="ml-auto text-gray-400 shrink-0">
                      {file instanceof File
                        ? file.size < 1024 * 1024
                          ? `${(file.size / 1024).toFixed(1)} KB`
                          : `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                        : null}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-5 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 shrink-0">
          <button
            onClick={onCancel}
            className="flex-1 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
          >
            Go Back
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className="flex-1 py-2 rounded-lg text-sm font-semibold text-white bg-primary-700 dark:bg-primary-800 hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 disabled:dark:opacity-50 transition-colors shadow-sm flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  />
                </svg>
                Submitting…
              </>
            ) : (
              <>
                <PaperAirplaneIcon className="w-3.5 h-3.5" />
                Submit Report
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubmitConfirmDialog;
